/**
 * Webex Contacts Integration - Public API
 * 
 * Standalone module for personal contacts enrichment.
 * Completely decoupled from DSS implementation and controlled by feature flags.
 * 
 * Usage Example:
 * ```typescript
 * import { createContactsIntegration, isContactsEnabled } from './contacts';
 * import { CONTACTS_FEATURE_FLAGS } from '../../config';
 * 
 * // Check if enabled
 * if (isContactsEnabled()) {
 *   // Initialize
 *   const contacts = await createContactsIntegration(webex, userId);
 *   
 *   // Lookup
 *   const result = contacts.resolver.resolve('+14155551234');
 *   if (result) {
 *     console.log(result.displayName); // Personal contact name
 *   }
 *   
 *   // Cleanup
 *   contacts.dispose();
 * }
 * ```
 */

import { WebexContactsManager } from './contactsManager';
import { ContactsResolver } from './contactsResolver';
import { ContactsCacheManager } from './contactsCache';
import { CONTACTS_FEATURE_FLAGS } from '../../../config';

// Re-export all types for convenience
export type {
  WebexContact,
  ContactPhoneNumber,
  ContactEmail,
  ContactEntry,
  WebexContactsCache,
  ContactsAPIResponse,
  ContactsCacheStats,
  ContactsPerformanceMetrics,
  ContactsManagerConfig,
  ContactsTelemetryEvent,
  ContactsTelemetryHandler,
  ContactsLookupResult,
  ContactsBatchLookupResult,
  ContactsFeatureFlags,
} from './types';

// Re-export classes
export { WebexContactsManager } from './contactsManager';
export { ContactsResolver } from './contactsResolver';
export { ContactsCacheManager } from './contactsCache';

/**
 * Contacts integration instance
 * Combines manager and resolver for easy use
 */
export interface ContactsIntegration {
  manager: WebexContactsManager;
  resolver: ContactsResolver;
  dispose: () => void;
}

/**
 * Check if contacts enrichment is enabled
 * Controlled by feature flag
 */
export function isContactsEnabled(): boolean {
  return CONTACTS_FEATURE_FLAGS.ENABLE_CONTACTS_ENRICHMENT;
}

/**
 * Get current feature flags
 */
export function getContactsFeatureFlags() {
  return { ...CONTACTS_FEATURE_FLAGS };
}

/**
 * Create and initialize contacts integration
 * 
 * @param webex - Webex SDK instance
 * @param userId - User ID for cache scoping
 * @param options - Optional configuration overrides
 * @returns ContactsIntegration instance
 * 
 * @throws Error if contacts feature is disabled
 * @throws Error if webex or userId is invalid
 */
export async function createContactsIntegration(
  webex: any,
  userId: string,
  options?: {
    cacheTTL?: number;
    autoRefresh?: boolean;
    enableTelemetry?: boolean;
  }
): Promise<ContactsIntegration> {
  if (!isContactsEnabled()) {
    throw new Error('Contacts enrichment is disabled by feature flag');
  }

  if (!webex) {
    throw new Error('Webex SDK instance is required');
  }

  if (!userId) {
    throw new Error('User ID is required');
  }

  // Create manager with feature flag config
  const manager = new WebexContactsManager(webex, {
    userId,
    cacheTTL: options?.cacheTTL ?? CONTACTS_FEATURE_FLAGS.CONTACTS_CACHE_TTL_MS,
    autoRefresh: options?.autoRefresh ?? CONTACTS_FEATURE_FLAGS.CONTACTS_AUTO_REFRESH,
    enableTelemetry: options?.enableTelemetry ?? CONTACTS_FEATURE_FLAGS.CONTACTS_ENABLE_TELEMETRY,
  });

  // Initialize (non-blocking if cache exists)
  await manager.initialize();

  // Create resolver
  const resolver = new ContactsResolver(manager);

  return {
    manager,
    resolver,
    dispose: () => {
      manager.dispose();
    },
  };
}

/**
 * Create contacts integration with graceful error handling
 * Returns null if initialization fails (allows fallback to DSS-only)
 * 
 * Recommended for production use with fallback logic.
 */
export async function createContactsIntegrationSafe(
  webex: any,
  userId: string,
  options?: {
    cacheTTL?: number;
    autoRefresh?: boolean;
    enableTelemetry?: boolean;
  }
): Promise<ContactsIntegration | null> {
  try {
    const result = await createContactsIntegration(webex, userId, options);
    return result;
  } catch (error: any) {
    console.error('[Enrichment] Contacts: Failed to create integration:', error.message);
    return null;
  }
}

/**
 * Helper to check if contacts integration is available and ready
 */
export function isContactsReady(integration: ContactsIntegration | null): boolean {
  return integration !== null && integration.resolver.isReady();
}

/**
 * Helper to get contacts statistics
 */
export function getContactsStats(integration: ContactsIntegration | null) {
  if (!integration) {
    return {
      enabled: false,
      ready: false,
      totalContacts: 0,
      totalPhones: 0,
    };
  }

  const stats = integration.manager.getStats();
  return {
    enabled: true,
    ready: integration.resolver.isReady(),
    totalContacts: stats.totalContacts,
    totalPhones: stats.totalPhones,
    cacheAge: Date.now() - stats.fetchedAt,
    expiresIn: stats.expiresAt - Date.now(),
  };
}
